import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CompanyContextForm } from './CompanyContextForm';
import { onboardingService } from '@/services/onboardingService';
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface CompanyContextStepProps {
  isComplete: boolean;
  onComplete: () => void;
}

export function CompanyContextStep({ isComplete, onComplete }: CompanyContextStepProps) {
  const t = useTranslations('consultantOnboarding.companyContext.step');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (
    contextText: string,
    fiscalYear: string,
    fiscalYearStartDate: string,
    fiscalYearEndDate: string,
    accountingMethod: string,
    companyType: string,
    vatRegistered: boolean,
    fSkatt: boolean,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await onboardingService.saveCompanyContext(
        contextText, fiscalYear, fiscalYearStartDate, fiscalYearEndDate, accountingMethod, companyType, vatRegistered, fSkatt,
      );
      if (result.success) {
        onComplete();
      } else {
        setError(result.error || t('saveFailed'));
      }
    } catch (err) {
      setError(userFacingErrorMessage(err, t('unexpected')));
    } finally {
      setIsLoading(false);
    }
  };

  if (isComplete) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <CardTitle className="text-green-700">{t('completeTitle')}</CardTitle>
          </div>
          <CardDescription>
            {t('completeBody')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <CompanyContextForm onSave={handleSave} isLoading={isLoading} />
      </CardContent>
    </Card>
  );
}
